import React, { useEffect, useMemo } from 'react'
import { Divider, usePageWidth } from '@fs/zion-ui'
import { Link, useLocation } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import Subnav from '@fs/zion-tree-subnav-legacy'
import { useExperiment } from '@fs/zion-experiments'

const navBarCss = css`
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  white-space: nowrap;
  scroll-behavior: smooth;
  line-height: 2.5rem;
  padding-inline-start: 10px;
  z-index: 999;
  background-color: white;
`

const spanCss = css`
  font-family: Verdana, Ayuthaya, sans-serif;
  color: #000000;
  text-decoration: none;
  background: transparent;
  border-radius: 4px;
  border: 0;
  cursor: pointer;
  font-size: 14px;
  margin-inline-end: 5px;
  padding: 4px 10px;
  vertical-align: middle;

  [lang|='zh'] &,
  [lang|='ja'] &,
  [lang|='ko'] & {
    font-size: 16px;
  }

  &:hover {
    background: #e1edc1;
    text-decoration: underline; /* required or else the <a> tag will underline with a color */
  }
  &:focus {
    outline: none;
  }
  &[data-is-selected='true'] {
    background: #e1edc1;
  }
`
const navLinkCss = css`
  text-decoration: none;
`

const untranslatedBooksLangs = [
  'zh',
  'zh-hans',
  'ja',
  'ko',
  'th',
  'da',
  'hu',
  'nl',
  'pl',
  'sv',
  'sk',
  'fi',
  'mn',
  'bg',
  'km',
  'id',
  'ro',
  'sm',
  'to',
  'vi',
  'fj',
  'sq',
  'cs',
  'no',
]

function NavLink({ path, name, external, selected }) {
  return (
    <Link to={path} external={external} noLinkStyles css={navLinkCss}>
      <span css={spanCss} data-is-selected={selected}>
        {name}
      </span>
    </Link>
  )
}

export default function NavigationTabs() {
  const { pathname } = useLocation()
  const atPageWidth = usePageWidth()
  const tabletDesktop = atPageWidth({ default: false, xxl: true })
  const [navigationTabsEx] = useExperiment('navigationTabsEx')

  const isNavShown = useMemo(() => {
    return (pathname.includes('images') || pathname.includes('beta')) && tabletDesktop
  }, [pathname, tabletDesktop])

  useEffect(() => {
    if (isNavShown) document.documentElement.style.setProperty('--subnav-height', '42px')
    else document.documentElement.style.setProperty('--subnav-height', '0px')
  }, [isNavShown])

  if (pathname.includes('tree')) {
    return <Subnav page="find" />
  }

  return isNavShown && navigationTabsEx ? <NavigationTabsInner /> : null
}

function NavigationTabsInner() {
  const { t } = useTranslation()
  // Books and Wiki are hidden for CJK languages at the request of FS CJK specialists
  const hideBooksLangs = untranslatedBooksLangs.indexOf(i18n.language) >= 0

  function navLink({ path, name, external = true, selected = false }) {
    return <NavLink path={path} name={name} external={external} selected={selected} />
  }

  return (
    <>
      <nav css={navBarCss} id="navigation-tabs">
        {navLink({ path: '/search', name: t('search.tab.records', 'Records') })}
        {navLink({ path: '/records/images', name: t('search.tab.images', 'Images'), selected: true })}
        {navLink({
          path: '/search/tree/name',
          name: t('search.tab.tree', 'Family Tree'),
        })}
        {navLink({
          path: '/search/genealogies',
          name: t('search.tab.genealogies', 'Genealogies'),
        })}
        {navLink({ path: '/search/catalog', name: t('search.tab.catalog', 'Catalog') })}
        {!hideBooksLangs && navLink({ path: '/library/books', name: t('search.tab.books', 'Books') })}
        {!hideBooksLangs &&
          navLink({
            path: t('search.tab.wikiLink', '/en/wiki/Main_Page'),
            name: t('search.tab.wiki', 'Wiki'),
          })}
      </nav>
      <Divider />
    </>
  )
}
